import {RunInAction} from "mobx-graphlink";
import React from "react";
import {Button, CheckBox, Column, Row} from "react-vcomponents";
import {BoxController, ShowMessageBox} from "react-vmessagebox";
import {MapObject, ShapeType} from "../../../../../Server/DB/mapObjects/@MapObject.js";
import {ObjMesh} from "../../../../../Server/DB/mapObjects/@MapObject/ObjMesh.js";
import {ObjPath} from "../../../../../Server/DB/mapObjects/@MapObject/ObjPath.js";
import {ResolvePathMeshes} from "../../../../../Server/DB/mapObjects/@MapObject/ObjPath/ObjPathResolvers.js";
import {GetObjAncestors, IsMapObjectHidden, ResolveObjPosInWorld, ResolveObjRotInWorld} from "../../../../../Server/DB/mapObjects/$mapObject.js";
import {DMap} from "../../../../../Server/DB/maps/@Map.js";
import {MapState} from "../../../../../Store/main/maps/@MapState.js";
import {Vec2, Vec3} from "../../../../../Utils/ThreeJS/VectorStructs.js";
import {MapSurface_current} from "../../MapSurface.js";
import {OutlinerScrollView_Current} from "../../OutlinerUI.js";
import {AssertUnreachable} from "web-vcore";
import {ApproximateObjWorldRect} from "../../../../../Terrain/Heights/TerrainExporter.js";

type MatchedRenderable = {
	mesh?: ObjMesh,
	path?: ObjPath,
	shape?: boolean,
	disabled: boolean,
};
export type OverlapEntry = {obj: MapObject, matchedRenderables: MatchedRenderable[], hidden: boolean};

export function ShowObjOverlapsDialog(args: {map: DMap, mapState: MapState, objects: MapObject[], point_world: Vec3}) {
	const {objects, point_world} = args;
	const entries = GetObjectsOverlappingPoint(objects, point_world);
	if (entries.length == 0) {
		ShowMessageBox({
			title: "Overlapping objects", message: "No objects overlap this point.",
			okButton: false, cancelButton: true, cancelButtonProps: {text: "Close"},
		});
		return;
	}

	const ctrl: {current: BoxController|n} = {current: null};
	const dialogState = {includeHidden: true, closeOnAction: true};

	function DoAction(obj: MapObject, action: "focus"|"reveal"|"select", isLast = true) {
		const {mapState} = args;
		if (action == "focus") {
			MapSurface_current?.FocusObject(obj);
		} else if (action == "reveal") {
			RunInAction("ShowOverlaps.revealInOutliner", ()=>{
				for (const ancestor of GetObjAncestors(obj)) {
					mapState.expandedObjects[ancestor.id] = true;
				}
			});
			// wait for React to render the (now-expanded) outliner before scrolling to the entry
			requestAnimationFrame(()=>requestAnimationFrame(()=>{
				ScrollOutlinerToObject(obj.id);
			}));
		} else if (action == "select") {
			RunInAction("ShowOverlaps.select", ()=>{
				mapState.selectedObjects = [obj.id];
				mapState.activeObject = obj.id;
			});
		} else {
			AssertUnreachable(action);
		}
		if (isLast && dialogState.closeOnAction) {
			ctrl.current?.Close();
		}
	}

	const Content = ({updateCallCount}: {updateCallCount: number})=>{
		void updateCallCount; // re-render on checkbox toggle (via controller.UpdateUI)
		const filtered = dialogState.includeHidden ? entries : entries.filter(entry=>!entry.hidden);
		return (
			<Column style={{minWidth: 420}}>
				<Row>
					<CheckBox text="Include hidden" value={dialogState.includeHidden} onChange={val=>{
						dialogState.includeHidden = val; ctrl.current?.UpdateUI();
					}}/>
					<CheckBox ml={20} text="Close on action" value={dialogState.closeOnAction} onChange={val=>{
						dialogState.closeOnAction = val; ctrl.current?.UpdateUI();
					}}/>
				</Row>
				{filtered.length == 0 && <div style={{marginTop: 10}}>No visible objects overlap this point.</div>}
				{filtered.map(entry=>(
					<Row key={entry.obj.id} style={{marginTop: 8, alignItems: "center"}}>
						<span style={{flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>{entry.obj.name}</span>
						<Button text="Focus (map)" onClick={()=>DoAction(entry.obj, "focus")}/>
						<Button ml={5} text="Reveal (outliner)" onClick={()=>DoAction(entry.obj, "reveal")}/>
						<Button ml={5} text="Reveal + select" onClick={()=>{
							DoAction(entry.obj, "reveal", false);
							DoAction(entry.obj, "select");
						}}/>
					</Row>
				))}
			</Column>
		);
	};

	ctrl.current = ShowMessageBox({
		title: `Overlapping objects (${entries.length})`,
		message: Content,
		okButton: false, cancelButton: true, cancelButtonProps: {text: "Close"},
		containerStyle: {minWidth: 500, maxHeight: "70%"},
	});
}

function ScrollOutlinerToObject(objID: string) {
	const sv = OutlinerScrollView_Current();
	if (sv == null) return;
	const sdom = sv.vScrollableDOM as HTMLElement|n;
	if (sdom == null) return;
	const entryEl = sdom.querySelector(`[data-obj-id="${objID}"]`) as HTMLElement|n;
	if (entryEl == null) return;
	const currentScroll = sdom.scrollTop;
	const entryTopRelative = entryEl.getBoundingClientRect().top - sdom.getBoundingClientRect().top + currentScroll;
	const viewHeight = sdom.clientHeight;
	const target = Math.max(0, entryTopRelative - (viewHeight - entryEl.offsetHeight) / 2);
	sv.SetScroll({x: 0, y: target});
}

export function GetObjectsOverlappingPoint(objects: MapObject[], point_world: Vec3): OverlapEntry[] {
	const result: OverlapEntry[] = [];
	for (const obj of objects) {
		const objPos = ResolveObjPosInWorld(obj);
		if (objPos == null) continue;
		const objRot = ResolveObjRotInWorld(obj) ?? 0;
		const local = WorldPointToObjLocal(point_world, objPos, objRot);

		const matched: MatchedRenderable[] = [];
		for (const mesh of obj.meshes ?? []) {
			if (mesh.points?.length >= 3 && PointInPolygon(local, mesh.points)) {
				matched.push({mesh, disabled: !!mesh.renderDisabled});
			}
		}
		if (obj.shape_type && obj.shape_size) {
			if (InBasicShape(local, obj.shape_type, obj.shape_size)) {
				matched.push({shape: true, disabled: false});
			}
		}
		for (const path of obj.paths ?? []) {
			if (PathContainsPoint(path, obj, local)) {
				matched.push({path, disabled: !!path.renderDisabled});
			}
		}
		if (matched.length == 0) continue;

		const objHidden = IsMapObjectHidden(obj, objects);
		const hidden = objHidden || matched.every(m=>m.disabled);
		result.push({obj, matchedRenderables: matched, hidden});
	}
	return result.OrderBy(a=>{
		const rect = ApproximateObjWorldRect(a.obj);
		return rect.width * rect.height;
	});
}

/** Convert a world-space point into the object's local (2D, xy) frame, mirroring how MapObjectUI renders:
 *  world = R(-rot)·local + pos  →  local = R(+rot)·(world − pos). */
function WorldPointToObjLocal(point_world: Vec3, objPos_world: Vec3, rotWorld_deg: number): Vec2 {
	const offsetX = point_world.x - objPos_world.x;
	const offsetY = point_world.y - objPos_world.y;
	const theta = rotWorld_deg * (Math.PI / 180);
	const cos = Math.cos(theta);
	const sin = Math.sin(theta);
	return new Vec2(offsetX * cos - offsetY * sin, offsetX * sin + offsetY * cos);
}

function PathContainsPoint(path: ObjPath, obj: MapObject, local: Vec2): boolean {
	const resolved = ResolvePathMeshes(path, obj);
	if (resolved == null || resolved.meshes.length == 0) return false;
	const closed = resolved.closed;
	const halfWidth = Math.max((path.width ?? 1) / 2, 0.001);
	for (const meshSet of resolved.meshes) {
		if (meshSet.length < 2) continue;
		if (closed) {
			if (meshSet.length >= 3 && PointInPolygon(local, meshSet)) return true;
		} else {
			for (let i = 0; i < meshSet.length - 1; i++) {
				if (DistanceToSegment(local, meshSet[i], meshSet[i + 1]) <= halfWidth) return true;
			}
		}
	}
	return false;
}

function InBasicShape(local: Vec2, type: ShapeType, size: Vec2): boolean {
	if (type == ShapeType.ellipse) {
		const rx = (size.x ?? 0) / 2;
		const ry = (size.y ?? 0) / 2;
		if (rx <= 0 || ry <= 0) return false;
		return (local.x * local.x) / (rx * rx) + (local.y * local.y) / (ry * ry) <= 1;
	}
	const w = (size.x ?? 0) / 2;
	const h = (size.y ?? 0) / 2;
	return Math.abs(local.x) <= w && Math.abs(local.y) <= h;
}

function PointInPolygon(point: Vec2, poly: {x: number, y: number}[] | [number, number][]): boolean {
	const PX = (p: any)=>Array.isArray(p) ? p[0] : p.x;
	const PY = (p: any)=>Array.isArray(p) ? p[1] : p.y;
	let inside = false;
	for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
		const xi = PX(poly[i]), yi = PY(poly[i]);
		const xj = PX(poly[j]), yj = PY(poly[j]);
		const intersect = ((yi > point.y) != (yj > point.y)) && (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);
		if (intersect) inside = !inside;
	}
	return inside;
}

function DistanceToSegment(point: Vec2, a: {x: number, y: number} | [number, number], b: {x: number, y: number} | [number, number]): number {
	const AX = (p: any)=>Array.isArray(p) ? p[0] : p.x;
	const AY = (p: any)=>Array.isArray(p) ? p[1] : p.y;
	const ax = AX(a), ay = AY(a);
	const bx = AX(b), by = AY(b);
	const dx = bx - ax, dy = by - ay;
	const lenSq = dx * dx + dy * dy;
	let t = lenSq == 0 ? 0 : ((point.x - ax) * dx + (point.y - ay) * dy) / lenSq;
	t = Math.max(0, Math.min(1, t));
	return Math.hypot(point.x - (ax + t * dx), point.y - (ay + t * dy));
}